import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { AgGridReact } from "ag-grid-react";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";

import { fetchPayouts } from "../../../redux/features/sales/payoutsSlice";
import { fetchHotels } from "../../../redux/features/hotels/hotelsSlice";
import { fetchCondos } from "../../../redux/features/condos/condosSlice";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 220,
  },
}));

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const PayoutsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { mode } = useSelector((state) => state.ui);
  const { payoutsList, isLoading, error } = useSelector(
    (state) => state.payouts
  );
  const { hotelsList } = useSelector((state) => state.hotels);
  const { condosList } = useSelector((state) => state.condos);

  const [propertyType, setPropertyType] = useState("hotel");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const gridApiRef = useRef(null);

  useEffect(() => {
    if (location.state?.showSuccess) {
      setSnackbar({
        open: true,
        message:
          location.state.snackbarContent || "Payout created successfully!",
        severity: "success",
      });
      history.replace(location.pathname, {});
    }
  }, [history, location]);

  useEffect(() => {
    dispatch(fetchHotels());
    dispatch(fetchCondos());
  }, [dispatch]);

  useEffect(() => {
    const hotelId =
      propertyType === "hotel" ? selectedPropertyId || undefined : undefined;
    const condoId =
      propertyType === "condo" ? selectedPropertyId || undefined : undefined;
    dispatch(fetchPayouts({ hotelId, condoId, type: propertyType }));
  }, [dispatch, propertyType, selectedPropertyId]);

  const rowData = useMemo(() => payoutsList || [], [payoutsList]);
  const rowCount = rowData.length;
  const errorMessage = error?.errorMessage || "";

  const ActionCellRenderer = useCallback(
    (params) => {
      const id = params.data?.payment_id;
      if (!id) return null;
      return (
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          color="primary"
          onClick={() => history.push(`/sales/payouts/${id}`)}
        >
          View
        </Button>
      );
    },
    [history]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Payout ID",
        field: "payment_id",
        filter: "agNumberColumnFilter",
        width: 120,
        pinned: "left",
      },
      {
        headerName: "Date",
        field: "created_at",
        minWidth: 180,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Property",
        field: "hotel_name",
        filter: "agTextColumnFilter",
        minWidth: 180,
      },
      {
        headerName: "Bank Account",
        field: "stripe_account_name",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Orders Total",
        field: "total_orders_amount",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Commission",
        field: "commission_amount",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Transfer Amount",
        field: "total_paid",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
      {
        headerName: "Status",
        field: "payment_status",
        filter: "agTextColumnFilter",
        valueFormatter: (params) =>
          params.value ? params.value.toUpperCase() : "",
        cellStyle: (params) => {
          if (params.value === "complete") {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }
          return undefined;
        },
      },
      {
        headerName: "Comments",
        field: "comments",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Stripe Transfer",
        field: "stripe_transfer_id",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Actions",
        field: "payment_id",
        pinned: "right",
        width: 120,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [ActionCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 120,
    }),
    []
  );

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;
      if (isLoading) params.api.showLoadingOverlay();
      else if (!rowCount || error) params.api.showNoRowsOverlay();
    },
    [error, isLoading, rowCount]
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (isLoading) {
      api.showLoadingOverlay();
      return;
    }
    if (error || rowCount === 0) {
      api.showNoRowsOverlay();
      return;
    }
    api.hideOverlay();
  }, [error, isLoading, rowCount]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.headerRow}>
          <Typography variant="h6">Payouts</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push("/sales/payouts/new")}
          >
            New Payout
          </Button>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.filterRow}>
            <ToggleButtonGroup
              value={propertyType}
              exclusive
              size="small"
              onChange={(_, val) => {
                if (!val) return;
                setPropertyType(val);
                setSelectedPropertyId("");
              }}
            >
              <ToggleButton value="hotel">Hotel</ToggleButton>
              <ToggleButton value="condo">Condo</ToggleButton>
            </ToggleButtonGroup>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <InputLabel>
                {propertyType === "hotel"
                  ? "Filter by Hotel"
                  : "Filter by Condo"}
              </InputLabel>
              <Select
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                label={
                  propertyType === "hotel"
                    ? "Filter by Hotel"
                    : "Filter by Condo"
                }
              >
                <MenuItem value="">
                  {propertyType === "hotel" ? "All Hotels" : "All Condos"}
                </MenuItem>
                {propertyType === "hotel"
                  ? hotelsList.map((h) => (
                      <MenuItem key={h.id} value={h.id}>
                        {h.name}
                      </MenuItem>
                    ))
                  : condosList.map((c) => (
                      <MenuItem key={c.condos_id} value={c.condos_id}>
                        {c.name}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div
            className={
              mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
            }
            style={{ height: 600, width: "100%" }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection
              animateRows
              overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading payouts...</span>"
              overlayNoRowsTemplate={
                errorMessage
                  ? `<span class='ag-overlay-no-rows-center'>${errorMessage}</span>`
                  : "<span class='ag-overlay-no-rows-center'>No payouts to display</span>"
              }
              onGridReady={onGridReady}
            />
          </div>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default PayoutsPage;
